window.__nwd = 'v7';
/* Northwest Deals — product page behaviour
   1. Sticky add-to-cart bar (shows when the main button scrolls off-screen)
   2. Keeps the bar in sync with the selected variant via the theme's pub/sub
*/
(function () {
  if (customElements.get('nwd-sticky-bar')) return;

  class NwdStickyBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.watchId = this.dataset.watch;
      this.button = this.querySelector('[data-nwd-sticky-submit]');
      this.priceEl = this.querySelector('[data-nwd-sticky-price]');
      this.variantEl = this.querySelector('[data-nwd-sticky-variant]');
      this.imgEl = this.querySelector('[data-nwd-sticky-image]');
      this.labelEl = this.querySelector('[data-nwd-sticky-label]');

      this.hidden = false;
      this.classList.remove('is-visible');

      // iOS Safari can swallow the first click near its collapsed toolbar;
      // act on pointerup and ignore the follow-up click for a moment.
      this.button?.addEventListener('pointerup', (e) => { if (e.button === 0) this.submit(); });
      this.button?.addEventListener('click', () => this.submit());
      this.observeMainButton();
      this.subscribeToVariant();
      this.watchDom();
      this.syncFromDom();
      document.addEventListener('nwd:mode', () => this.refresh());
    }

    get subscribeState() {
      const picker = this.root?.querySelector('variant-selects[data-sub-pct]');
      if (!picker || !picker.classList.contains('is-subscribe')) return { on: false, pct: 0 };
      return { on: true, pct: parseFloat(picker.dataset.subPct) || 0 };
    }

    refresh() {
      if (this._variant) this.update(this._variant);
    }

    get root() {
      return document.getElementById('MainProduct-' + this.sectionId) || document.querySelector('product-info.nwd-pdp');
    }

    // Source of truth: the selected-variant JSON Dawn re-renders inside <variant-selects>.
    syncFromDom() {
      const json = this.root?.querySelector('variant-selects [data-selected-variant]');
      if (!json) return;
      try {
        const v = JSON.parse(json.textContent);
        if (v && v.id !== this._lastVariantId) {
          this._lastVariantId = v.id;
          this.update(v);
        }
      } catch (_) {}
    }

    watchDom() {
      const root = this.root;
      if (!root) return;
      // Dawn swaps <variant-selects> after each change; re-sync when it lands
      this.mo = new MutationObserver(() => {
        clearTimeout(this._syncT);
        this._syncT = setTimeout(() => this.syncFromDom(), 80);
      });
      this.mo.observe(root, { childList: true, subtree: true });
      // Also react to the click itself (fast path), then again after the fetch settles
      root.addEventListener('change', (e) => {
        if (!e.target.matches?.('.nwd-plan__input, variant-selects input, variant-selects select')) return;
        setTimeout(() => this.syncFromDom(), 50);
        setTimeout(() => this.syncFromDom(), 900);
      });
    }

    get mainButton() {
      return document.getElementById(this.watchId);
    }

    observeMainButton() {
      const target = this.mainButton;
      if (!target || !('IntersectionObserver' in window)) return;
      this.io = new IntersectionObserver(
        (entries) => {
          const e = entries[0];
          // show only once the button has scrolled *above* the viewport
          const above = !e.isIntersecting;
          this.classList.toggle('is-visible', above);
        },
        { threshold: 0 }
      );
      this.io.observe(target);
    }

    subscribeToVariant() {
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || data.sectionId !== this.sectionId) return;
          this.update(data.variant);
          // section re-render replaced the main button; re-observe
          this.io?.disconnect();
          this.observeMainButton();
        });
        return;
      }
      // Fallback: read the selected-variant JSON after any picker change
      const picker = document.getElementById('variant-selects-' + this.sectionId);
      picker?.addEventListener('change', () => {
        setTimeout(() => {
          const json = picker.querySelector('[data-selected-variant]');
          if (json) {
            try { this.update(JSON.parse(json.textContent)); } catch (_) {}
          }
        }, 600);
      });
    }

    update(variant) {
      if (!variant) {
        this.button.disabled = true;
        this.labelEl.textContent = window.variantStrings?.unavailable || 'Unavailable';
        return;
      }
      this._variant = variant;
      const sub = this.subscribeState;
      const price = sub.on ? Math.round(variant.price * (100 - sub.pct)) / 100 : variant.price;
      this.priceEl.textContent = this.money(price);
      const name = (variant.title || '').split('(')[0].trim();
      this.variantEl.textContent = sub.on ? name + ' · subscription' : name;
      const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
      if (this.imgEl && src) {
        this.imgEl.src = src.includes('?') ? src : src + '?width=96';
      }
      const available = !!variant.available;
      this.button.disabled = !available;
      this.labelEl.textContent = available
        ? (window.variantStrings?.addToCart || 'Add to cart')
        : (window.variantStrings?.soldOut || 'Sold out');
    }

    money(cents) {
      // Mirrors the theme's money format closely enough for a sticky bar;
      // the authoritative price is in the buy box.
      const fmt = window.themeMoneyFormat || window.Shopify?.money_format || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/, withCommas)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/<[^>]*>/g, '');
    }

    submit() {
      const btn = this.mainButton;
      if (!btn || btn.disabled) return;
      const now = Date.now();
      if (this._lastSubmit && now - this._lastSubmit < 1000) return; // de-dupe pointerup + click
      this._lastSubmit = now;
      const form = btn.form || btn.closest('form');
      if (form && typeof form.requestSubmit === 'function') form.requestSubmit(btn);
      else btn.click();
      this.button.classList.add('is-busy');
      setTimeout(() => this.button.classList.remove('is-busy'), 800);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.mo?.disconnect();
      this.unsub?.();
    }
  }

  customElements.define('nwd-sticky-bar', NwdStickyBar);
})();


/* 3. Purchase-mode toggle: drives Seal Subscriptions' widget from the plan cards */
(function () {
  const SEAL = {
    container: '.sealsubs-container',
    radio: 'input.sls-option',
    line: '.sls-selling-plan-group-first-line[role="radio"]',
    select: 'select.sls-select',
  };

  function findSeal(root) {
    const c = root.querySelector(SEAL.container);
    if (!c) return null;
    const radios = Array.from(c.querySelectorAll(SEAL.radio));
    const one = radios.find((r) => r.dataset.type === 'one_time');
    const sub = radios.find((r) => r.dataset.type === 'subscription');
    if (!one || !sub) return null;
    return { c, one, sub, select: c.querySelector(SEAL.select) };
  }

  function pick(radio) {
    if (radio.checked) return;
    const line = radio.closest(SEAL.line);
    if (line) line.click();
    if (!radio.checked) {
      radio.click();
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // Match "6" -> option text "6 months"; 1 -> "month"; 12 -> "year"
  function matchInterval(select, months) {
    if (!select) return false;
    const want = months === 1 ? /^month$/ : months === 12 ? /^(year|12 months)$/ : new RegExp('^' + months + ' months$');
    const opt = Array.from(select.options).find((o) => want.test(o.textContent.trim().toLowerCase()));
    if (!opt || select.value === opt.value) return !!opt;
    select.value = opt.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function selectedMonths(picker) {
    const r = picker.querySelector('.nwd-plan__input:checked');
    return r ? parseInt(r.dataset.months, 10) || 1 : 1;
  }

  function init(root) {
    const picker = root.querySelector('variant-selects[data-sub-pct]');
    const mode = picker && picker.querySelector('[data-nwd-mode]');
    if (!mode) return;
    // already wired, and Seal's widget hasn't been replaced since
    if (mode.dataset.ready && picker._sealC && root.contains(picker._sealC)) return;
    const seal = findSeal(root);
    if (!seal) return; // Seal not rendered (yet) -> its own widget stays visible

    mode.dataset.ready = '1';
    picker._sealC = seal.c;
    if (mode._abort) mode._abort.abort();
    mode._abort = new AbortController();
    const sig = { signal: mode._abort.signal };
    seal.c.classList.add('nwd-seal-slim');
    mode.hidden = false;

    const btns = Array.from(mode.querySelectorAll('[data-nwd-mode-btn]'));
    // Auto-match Seal's "Deliver every" select to the chosen supply.
    // The select itself stays visible and editable; buyer can override.
    function syncInterval() {
      if (!seal.select) return;
      matchInterval(seal.select, selectedMonths(picker));
    }

    function render(subscribe) {
      picker.classList.toggle('is-subscribe', subscribe);
      picker.querySelectorAll('[data-nwd-onetime]').forEach((el) => (el.hidden = subscribe));
      picker.querySelectorAll('[data-nwd-sub]').forEach((el) => (el.hidden = !subscribe));
      seal.c.classList.toggle('nwd-seal-onetime', !subscribe);
      root.dispatchEvent(new CustomEvent('nwd:mode', { bubbles: true, detail: { subscribe } }));
      btns.forEach((b) => {
        const on = (b.dataset.nwdModeBtn === 'subscribe') === subscribe;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
    }

    function setMode(subscribe) {
      pick(subscribe ? seal.sub : seal.one);
      if (subscribe) syncInterval();
      render(subscribe);
    }

    btns.forEach((b) => b.addEventListener('click', () => setMode(b.dataset.nwdModeBtn === 'subscribe'), sig));
    picker.addEventListener('change', (e) => {
      if (e.target.matches('.nwd-plan__input') && seal.sub.checked) syncInterval();
    }, sig);
    picker.setMode = setMode;

    render(seal.sub.checked);
    if (seal.sub.checked) syncInterval();
  }

  function wireJump(root) {
    root.querySelectorAll('[data-nwd-subscribe-jump]').forEach((el) => {
      if (el.dataset.wired) return;
      el.dataset.wired = '1';
      const go = () => {
        const picker = root.querySelector('variant-selects[data-sub-pct]');
        if (picker && picker.setMode) picker.setMode(true);
        (picker || root).scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      el.addEventListener('click', go);
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
      });
    });
  }

  function boot() {
    document.querySelectorAll('product-info.nwd-pdp').forEach((root) => {
      init(root);
      wireJump(root);
      if (!root.dataset.nwdObserved) {
        root.dataset.nwdObserved = '1';
        // Dawn swaps <variant-selects> after each variant change and Seal re-renders
        // its widget asynchronously; re-init whenever either lands.
        new MutationObserver(() => init(root)).observe(root, { childList: true, subtree: true });
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();


/* 4. Don't scroll the page to the gallery when a variant swaps its image.
   Dawn's media-gallery.setActiveMedia calls window.scrollTo when the active
   image is above the viewport; on a long buy box that yanks the user upward. */
(function () {
  customElements.whenDefined('media-gallery').then(() => {
    const proto = customElements.get('media-gallery').prototype;
    const original = proto.setActiveMedia;
    if (!original || original._nwdPatched) return;
    proto.setActiveMedia = function () {
      const realScrollTo = window.scrollTo;
      window.scrollTo = function (arg) {
        if (arg && typeof arg === 'object' && 'top' in arg) return; // swallow the vertical jump
        return realScrollTo.apply(window, arguments);
      };
      try {
        return original.apply(this, arguments);
      } finally {
        setTimeout(() => { window.scrollTo = realScrollTo; }, 400);
      }
    };
    proto.setActiveMedia._nwdPatched = true;
  });
})();

/* 5. Preselect the "most popular" plan on first load (no ?variant= in URL).
   Waits for Dawn's product-info / variant-selects to be defined, otherwise
   the change event fires before anyone is listening. */
(function () {
  function run() {
    if (/[?&]variant=/.test(location.search)) return;
    const picker = document.querySelector('variant-selects[data-default-popular]');
    if (!picker || picker.dataset.defaulted) return;
    const target = picker.querySelector('.nwd-plan__input[data-nwd-popular]');
    if (!target) { console.warn('[nwd] no variant carries the popular badge — check popular_match'); return; }
    picker.dataset.defaulted = '1';
    if (target.checked || target.classList.contains('disabled')) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true })); // Dawn fetches + re-renders
  }
  Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('variant-selects')])
    .then(() => {
      const go = () => setTimeout(run, 0);
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
      else go();
    });
})();
